import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FlowApp, FlowState, Lane } from '../data/cockpit';
import { clock, dayKey, dayLabel } from '../data/format';

/**
 * The connected flow timeline: a row per moment, a lane per system (Chargebee, connector, Exact), a card in the lane
 * that holds the document then, and a line along each document's way from card to card.
 *
 * Built the way the old scenario flow was: the cards are laid out by the grid, then measured, and the lines are drawn
 * between the measured edges, so a line starts on the card it leaves and its arrow ends on the card it goes into.
 */

export interface FlowDocument {
    id: string;
    label: string;
    color: string;
}

/** `healed`: it failed, but a later attempt got through; shown, not alarming. */
export type FlowLook = FlowState | 'fut' | 'healed';

export type FlowItem =
    | { phase: string }
    | { now: string }
    | {
          at?: string | null;
          documentId: string;
          lane: Lane;
          app?: FlowApp | string | null;
          look: FlowLook;
          title: string;
          detail?: string | null;
          /** Red line: the step back after a refusal (a 429, a failure). */
          tone?: 'bad';
          /** A check, not a step of the document: no line to it. */
          noEdge?: boolean;
          /** Happened in its own system without the connector, such as matching by hand in Exact: no connector step before it. */
          direct?: boolean;
          /** Only the day is known: shown without a time. */
          dayOnly?: boolean;
      };

/**
 * A card as drawn. On a connector card the app on the Chargebee side (cbrcv takes in, cbsnd sends out) is a tag on its
 * left, the app on the Exact side (exsnd sends out, exrcv takes in) a tag on its right: where the line comes in and
 * goes out.
 */
export type PlacedItem =
    | { phase: string }
    | { now: string }
    | (Omit<Extract<FlowItem, { documentId: string }>, 'app'> & { left?: string; right?: string; derived?: boolean });

export const DOC_COLORS = ['#5CC8FF', '#C4A8FF', '#3DDC97', '#FFC861', '#F28DC0', '#7FB8FF'];

const LANES: { lane: Lane; label: string; color: string }[] = [
    { lane: 'cb', label: 'Chargebee', color: '#FFA36B' },
    { lane: 'cn', label: 'Connector', color: '#7FB8FF' },
    { lane: 'ex', label: 'Exact', color: '#F28DC0' },
];

const MARK: Record<FlowLook, { ic: string; mark: string }> = {
    ok: { ic: 'ok', mark: '✓' },
    bad: { ic: 'bad', mark: '✕' },
    wait: { ic: 'warn', mark: '…' },
    busy: { ic: 'wait', mark: '↻' },
    fut: { ic: 'todo', mark: '' },
    healed: { ic: 'todo', mark: '✕' },
};

// ---------- everything through the connector ----------

const CHARGEBEE_SIDE = ['cbrcv', 'cbsnd'];
const EXACT_SIDE = ['exsnd', 'exrcv'];
const NOT_THERE: FlowLook[] = ['bad', 'wait', 'busy', 'healed'];

/**
 * Chargebee and Exact never talk to each other: every document goes through the connector, in through one app and out
 * through another. So between a Chargebee card and an Exact card there is always a connector card, with cbrcv on the
 * left and exsnd on the right (exrcv and cbsnd the other way); one the connector did not log but that must have been
 * there is drawn dashed. An app is only ever on a connector card. What Exact or Chargebee refused, or has not taken
 * yet, is still in the connector, at the app that offers it. A moment that happened in its system without the
 * connector gets no connector card and no line.
 */
export const throughConnector = (items: FlowItem[]): PlacedItem[] => {
    const out: PlacedItem[] = [];
    // Per document, its last card: where it is, and the card itself to tag the app it leaves through.
    const last = new Map<string, Extract<PlacedItem, { documentId: string }>>();

    items.forEach((item) => {
        if ('phase' in item || 'now' in item) {
            out.push(item);
            return;
        }

        const { app, ...rest } = item;
        const before = last.get(item.documentId);
        const prev = before?.lane;
        const it: Extract<PlacedItem, { documentId: string }> = { ...rest };

        if (it.lane !== 'cn' && NOT_THERE.includes(it.look)) {
            // Refused or still waiting: the connector holds it, at the app that offers it.
            if (it.lane === 'ex') {
                it.right = 'exsnd';
                if (prev === 'cb') it.left = 'cbrcv';
            } else {
                it.left = 'cbsnd';
                if (prev === 'ex') it.right = 'exrcv';
            }
            it.lane = 'cn';
        } else if (it.lane === 'cn') {
            if (app && CHARGEBEE_SIDE.includes(app)) it.left = app;
            if (app && EXACT_SIDE.includes(app)) it.right = app;
            if (prev === 'cb' && !it.left) it.left = 'cbrcv';
            if (prev === 'ex' && !it.right) it.right = 'exrcv';
            // Stuck on its way out: at the app that has to send it on.
            if (!app && NOT_THERE.includes(it.look)) {
                if (prev === 'ex') it.left = 'cbsnd';
                else it.right = 'exsnd';
            }
        }

        if (it.direct && prev && prev !== it.lane) {
            it.noEdge = true;
        } else if (prev && !it.noEdge) {
            if (prev === 'cb' && it.lane === 'ex') {
                out.push({ at: it.at, dayOnly: it.dayOnly, documentId: it.documentId, lane: 'cn', look: 'ok', title: 'Geboekt', left: 'cbrcv', right: 'exsnd', derived: true });
            } else if (prev === 'ex' && it.lane === 'cb') {
                out.push({ at: it.at, dayOnly: it.dayOnly, documentId: it.documentId, lane: 'cn', look: 'ok', title: 'Vastgelegd', left: 'cbsnd', right: 'exrcv', derived: true });
            } else if (prev === 'cn' && it.lane === 'ex' && before && !before.right) {
                before.right = 'exsnd';
            } else if (prev === 'cn' && it.lane === 'cb' && before && !before.left) {
                before.left = 'cbsnd';
            }
        }

        out.push(it);
        last.set(it.documentId, it);
    });

    return out;
};

// ---------- routing: pure, on measured boxes ----------

export interface Box {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

export interface LinkCard {
    doc: string;
    /** The lane from the left, 0 for Chargebee. */
    lane: number;
    box: Box;
    noEdge?: boolean;
    dashed?: boolean;
}

export interface LinkPath {
    doc: string;
    d: string;
    start: [number, number];
    dashed: boolean;
}

// How round a corner is, how far apart the lines of two documents run, and how wide all tracks together may be: well
// within the gap between two lanes' cards, so the last straight piece into a card is longer than its arrowhead.
const CORNER = 6;
const TRACK = 7;
const TRACKS_WIDTH = 16;

const crosses = (s: { x1: number; y1: number; x2: number; y2: number }, box: Box) => {
    const inset = 2;
    const [x1, x2] = [Math.min(s.x1, s.x2), Math.max(s.x1, s.x2)];
    const [y1, y2] = [Math.min(s.y1, s.y2), Math.max(s.y1, s.y2)];
    return x2 > box.left + inset && x1 < box.right - inset && y2 > box.top + inset && y1 < box.bottom - inset;
};

const pathThrough = (points: [number, number][]) => {
    let d = `M ${points[0][0]} ${points[0][1]}`;
    for (let i = 1; i < points.length - 1; i++) {
        const [px, py] = points[i - 1];
        const [x, y] = points[i];
        const [nx, ny] = points[i + 1];
        const into = Math.min(CORNER, Math.hypot(x - px, y - py) / 2);
        const out = Math.min(CORNER, Math.hypot(nx - x, ny - y) / 2);
        d += ` L ${x - Math.sign(x - px) * into} ${y - Math.sign(y - py) * into} Q ${x} ${y} ${x + Math.sign(nx - x) * out} ${y + Math.sign(ny - y) * out}`;
    }
    const [lx, ly] = points[points.length - 1];
    return `${d} L ${lx} ${ly}`;
};

/**
 * The line of each document from each of its cards to the next. Between two lanes a line leaves its card from the
 * side, runs down the gap before the next card's lane on the document's own track and goes into that card's side;
 * within a lane it goes straight down, or round the side when a card stands in between. Whichever way runs through
 * fewest other cards wins; a line does not leave a card from the side the previous line came in.
 *
 * @param borders the left edge of each lane, then the right edge of the last.
 */
export const routeLinks = (cards: LinkCard[], borders: number[], docOrder: string[]): LinkPath[] => {
    const ways = new Map<string, LinkCard[]>();
    cards.forEach((c) => ways.set(c.doc, [...(ways.get(c.doc) ?? []), c]));
    const paths: LinkPath[] = [];
    const boxes = cards.map((c) => c.box);
    const lastLane = borders.length - 2;
    const track = Math.min(TRACK, TRACKS_WIDTH / Math.max(docOrder.length - 1, 1));

    docOrder.forEach((doc, index) => {
        const way = ways.get(doc) ?? [];
        const offset = (index - (docOrder.length - 1) / 2) * track;
        let entered: 'left' | 'right' | 'top' | undefined;

        for (let i = 1; i < way.length; i++) {
            const prev = way[i - 1];
            const card = way[i];
            if (card.noEdge) continue;

            const from = prev.box;
            const to = card.box;
            const others = boxes.filter((b) => b !== from && b !== to);
            const hits = (points: [number, number][]) =>
                others.filter((b) => points.slice(1).some(([x2, y2], k) => crosses({ x1: points[k][0], y1: points[k][1], x2, y2 }, b))).length;
            const fromMid = (from.top + from.bottom) / 2;
            const toMid = (to.top + to.bottom) / 2;
            const routes: [number, number][][] = [];

            if (prev.lane === card.lane) {
                const x = (from.left + from.right) / 2 + offset;
                routes.push([
                    [x, from.bottom],
                    [x, to.top],
                ]);
                const sides = [...(prev.lane < lastLane ? ['right' as const] : []), ...(prev.lane > 0 ? ['left' as const] : [])];
                sides.forEach((side) => {
                    const gap = (side === 'right' ? borders[prev.lane + 1] : borders[prev.lane]) + offset;
                    const out = side === 'right' ? from.right : from.left;
                    const into = side === 'right' ? to.right : to.left;
                    routes.push([
                        [out, fromMid],
                        [gap, fromMid],
                        [gap, toMid],
                        [into, toMid],
                    ]);
                });
            } else {
                const right = card.lane > prev.lane;
                const gap = (right ? borders[card.lane] : borders[card.lane + 1]) + offset;
                const out = right ? from.right : from.left;
                const into = right ? to.left : to.right;
                routes.push([
                    [out, fromMid],
                    [gap, fromMid],
                    [gap, toMid],
                    [into, toMid],
                ]);
                const x = (from.left + from.right) / 2 + offset;
                routes.push([
                    [x, from.bottom],
                    [x, toMid],
                    [into, toMid],
                ]);
            }

            const leaves = (r: [number, number][]) => (r[0][0] === from.right ? 'right' : r[0][0] === from.left ? 'left' : 'bottom');
            const was = entered;
            const cost = (r: [number, number][]) => hits(r) + (leaves(r) === was ? 0.5 : 0);
            const best = routes.reduce((chosen, r) => (cost(r) < cost(chosen) ? r : chosen));
            const [endX] = best[best.length - 1];
            entered = endX === to.left ? 'left' : endX === to.right ? 'right' : 'top';
            paths.push({ doc, d: pathThrough(best), start: best[0], dashed: !!card.dashed });
        }
    });

    return paths;
};

// ---------- the component ----------

/** Measures the cards after layout and on every resize, and routes the lines between them. */
const useLinks = (deps: unknown[], docOrder: string[]) => {
    const ref = useRef<HTMLDivElement>(null);
    const [paths, setPaths] = useState<LinkPath[]>([]);

    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) return undefined;

        const measure = () => {
            const origin = element.getBoundingClientRect();
            const boxOf = (el: Element): Box => {
                const r = el.getBoundingClientRect();
                return { left: r.left - origin.left, right: r.right - origin.left, top: r.top - origin.top, bottom: r.bottom - origin.top };
            };
            const borders = [
                ...LANES.map(({ lane }) => (element.querySelector(`.tl-head .l-${lane}`)?.getBoundingClientRect().left ?? origin.left) - origin.left),
                origin.width,
            ];
            const cards: LinkCard[] = Array.from(element.querySelectorAll<HTMLElement>('[data-doc]')).map((el) => ({
                doc: el.dataset.doc!,
                lane: Number(el.dataset.lane),
                box: boxOf(el),
                noEdge: el.dataset.noedge === 'true',
                dashed: el.dataset.dashed === 'true',
            }));
            const next = routeLinks(cards, borders, docOrder);
            setPaths((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
        };

        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return { ref, paths };
};

/** Exact gives only a day for some moments: noon of that day keeps it on that day in every time zone. */
const dayOf = (at: string) => `${at.slice(0, 10)}T12:00:00Z`;

/** The time of a moment, with the day above it whenever the day changes; a day without a time is just the day. */
const When = ({ at, showDay, dayOnly }: { at?: string | null; showDay: boolean; dayOnly?: boolean }) =>
    dayOnly && at ? (
        <span className="tl-when">
            <small>{dayLabel(dayOf(at))}</small>
        </span>
    ) : (
        <span className="tl-when">
            {at && showDay && <small>{dayLabel(at)}</small>}
            {at ? clock(at) : ''}
        </span>
    );

export const FlowTimeline = ({ items: given, documents }: { items: FlowItem[]; documents: FlowDocument[] }) => {
    const [focus, setFocus] = useState<string | null>(null);
    const items = useMemo(() => throughConnector(given), [given]);
    const color = useMemo(() => new Map(documents.map((d) => [d.id, d.color])), [documents]);
    const docOrder = useMemo(() => documents.map((d) => d.id), [documents]);
    const { ref, paths } = useLinks([items, documents], docOrder);
    let lastDay = '';

    return (
        <div>
            {documents.length > 1 && (
                <div className="docs" role="group" aria-label="Toon de weg van" style={{ marginBottom: 10 }}>
                    <button className={focus === null ? 'docbtn on' : 'docbtn'} onClick={() => setFocus(null)}>
                        <i style={{ background: '#8A94A3' }} />
                        Alles
                    </button>
                    {documents.map((d) => (
                        <button key={d.id} className={focus === d.id ? 'docbtn on' : 'docbtn'} onClick={() => setFocus(focus === d.id ? null : d.id)}>
                            <i style={{ background: d.color }} />
                            {d.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="tl" ref={ref}>
                <svg className="tl-links" aria-hidden="true">
                    <defs>
                        {documents.map((d, i) => (
                            // In pixels, not in line widths: a fixed head that the last straight piece of a line is always longer than.
                            <marker key={d.id} id={`tl-head-${i}`} viewBox="0 0 10 10" refX="10" refY="5" markerUnits="userSpaceOnUse" markerWidth="9" markerHeight="9" orient="auto">
                                <path d="M 0 0 L 10 5 L 0 10 z" fill={d.color} />
                            </marker>
                        ))}
                    </defs>
                    {paths.map((p, i) => (
                        <path
                            key={i}
                            d={p.d}
                            fill="none"
                            stroke={color.get(p.doc) ?? '#8A94A3'}
                            strokeWidth={1.75}
                            strokeDasharray={p.dashed ? '5 4' : undefined}
                            markerEnd={`url(#tl-head-${docOrder.indexOf(p.doc)})`}
                            opacity={focus && focus !== p.doc ? 0.12 : 1}
                        />
                    ))}
                </svg>
                {/* The dot where a line leaves its card, over the cards so it is never half hidden. */}
                <svg className="tl-links tl-joints" aria-hidden="true">
                    {paths.map((p, i) => (
                        <circle key={i} cx={p.start[0]} cy={p.start[1]} r={3.25} fill={color.get(p.doc) ?? '#8A94A3'} opacity={focus && focus !== p.doc ? 0.12 : 1} />
                    ))}
                </svg>

                <div className="tl-row tl-head">
                    <span />
                    {LANES.map((l) => (
                        <span key={l.lane} className={`l-${l.lane}`} style={{ color: l.color }}>
                            {l.label}
                        </span>
                    ))}
                </div>

                {items.map((it, i) => {
                    if ('phase' in it) {
                        // Each document starts with its day.
                        lastDay = '';
                        return (
                            <div key={i} className="tl-row tl-phase">
                                <span />
                                <span className="span">{it.phase}</span>
                            </div>
                        );
                    }
                    if ('now' in it) {
                        return (
                            <div key={i} className="tl-row tl-now">
                                <span className="tl-when">NU {it.now}</span>
                                <span className="span">
                                    <i />
                                </span>
                            </div>
                        );
                    }

                    const day = it.at ? dayKey(it.dayOnly ? dayOf(it.at) : it.at) : lastDay;
                    const showDay = day !== lastDay;
                    lastDay = day;
                    const dim = !!focus && focus !== it.documentId;
                    const docColor = color.get(it.documentId) ?? '#8A94A3';

                    return (
                        <div key={i} className="tl-row">
                            <When at={it.at} showDay={showDay} dayOnly={it.dayOnly} />
                            {LANES.map(({ lane }, index) => (
                                <span key={lane} className={`tl-cell c-${lane}`}>
                                    {lane === it.lane && (
                                        <span
                                            className={`tl-card ${it.look} ${it.derived ? 'derived' : ''} ${dim ? 'dim' : ''}`}
                                            style={{ borderLeftColor: docColor }}
                                            data-doc={it.documentId}
                                            data-lane={index}
                                            data-noedge={it.noEdge ? 'true' : undefined}
                                            data-dashed={it.look === 'fut' || it.derived ? 'true' : undefined}
                                            title={it.derived ? `${it.title}\nNiet gelogd: moet gebeurd zijn tussen de stap ervoor en erna.` : it.detail ? `${it.title}\n${it.detail}` : it.title}
                                        >
                                            {it.left && <span className="apptag">{it.left}</span>}
                                            <span className={`ic ${MARK[it.look].ic}`}>{MARK[it.look].mark}</span>
                                            <span className="tx">
                                                <span className="ti">{it.title}</span>
                                                {it.detail && <span className="de">{it.detail}</span>}
                                            </span>
                                            {it.right && <span className="apptag">{it.right}</span>}
                                        </span>
                                    )}
                                </span>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
